import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../Context/firebaseConfig";
import { signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import { getUserToken } from "../api/user";
import SEO from "../Components/SEO";
import Ronaldo from "../images/Stable.jpg";
import { getAuthErrorMessage, logAuthError } from "../utils/firebaseAuthErrors";
import { getSafeNextPath, withNextPath } from "../utils/navigation";

export default function Login() {
    const [formData, setFormData] = useState({
        email: "",
        password: "",
    });

    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const nextPath = getSafeNextPath(searchParams.get("next"));
    const signupPath = nextPath.startsWith("/clipping-campaigns")
        ? "/signup?focus=clipping-campaigns"
        : "/signup";

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                navigate(nextPath);
            }
        });

        return () => unsubscribe();
    }, [navigate, nextPath]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        const email = formData.email.trim().toLowerCase();
        const password = formData.password;

        if (!email || !password) {
            setError("Enter your email and password to log in.");
            return;
        }

        try {
            setIsSubmitting(true);
            setError("");

            const userCredential = await signInWithEmailAndPassword(
                auth,
                email,
                password
            );

            getUserToken(userCredential.user).catch((tokenError) => {
                logAuthError("Login token fetch failed", tokenError, {
                    flow: "login",
                    userId: userCredential.user?.uid || "unknown",
                });
            });

            navigate(nextPath);
        } catch (error) {
            logAuthError("Login failed", error, {
                flow: "login",
                emailDomain: email.split("@")[1] || "unknown",
            });
            setError(
                getAuthErrorMessage(
                    error,
                    "Invalid email or password. Please try again."
                )
            );
            setIsSubmitting(false);
        }
    };

    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        formData.email
    );

    return (
        <>
            <SEO
                title="Log In | Kre8 Content"
                description="Log in to your Kre8 Content account to message creators, manage orders, and update your video editor or creator profile."
                canonicalPath="/login"
                noindex
            />
            {/* <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-center" style={{marginTop:'1%', marginBottom:'1%'}}>
                <p className="text-sm/6 text-gray-900 text-center" style={{marginLeft:'30%'}}>
                   Join our Discord to stay in the loop!
                </p>

                <a
                    href="https://discord.gg/GsTCqZkVjg"
                    className="flex-none rounded-full bg-gray-900 px-3.5 py-1 text-sm font-semibold text-white shadow-sm hover:bg-gray-700" style={{paddingTop:'1%',paddingBottom:'1%'}}
                >
                    Join Discord{" "}
                    <span aria-hidden="true">&rarr;</span>
                </a>
            </div> */}
            <div
                className="mobile-safe relative min-h-screen overflow-hidden bg-black text-white"
                style={{
                    backgroundImage: `url(${Ronaldo})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-black/75" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
                <div className="absolute -top-24 left-[-8rem] h-72 w-72 rounded-full bg-[#9146FF]/20 blur-3xl sm:h-[28rem] sm:w-[28rem]" />
                <div className="absolute bottom-[-8rem] right-[-7rem] h-72 w-72 rounded-full bg-[#84ff00]/10 blur-3xl sm:h-[24rem] sm:w-[24rem]" />
                <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-8 sm:py-10">
                    <div className="mb-7 max-w-[36rem] text-center sm:mb-10">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#84ff00] sm:text-sm sm:tracking-[0.2em]">
                            Welcome Back to Kre8
                        </p>

                        <h1 className="text-3xl font-black leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                            Create Videos at a
                            <span className="block bg-gradient-to-r from-[#84ff00] to-[#9146FF] bg-clip-text text-transparent">
                                Bigger Scale
                            </span>
                        </h1>

                        <p className="mx-auto mt-4 max-w-xl text-sm text-zinc-400 sm:mt-5 sm:text-lg">
                            Come back to growing your content business.
                        </p>

                    </div>
                    <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-black/45 p-5 shadow-2xl backdrop-blur-xl sm:p-8 lg:p-10">

                        <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                            {error && (
                                <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                                    {error}
                                </p>
                            )}
                            <div>
                                <label className="mb-2 block text-sm font-medium text-zinc-300">
                                    Email
                                </label>

                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    autoComplete="email"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck="false"
                                    inputMode="email"
                                    required
                                    placeholder="you@example.com"
                                    className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#84ff00]"
                                />
                                {!isEmailValid &&
                                    formData.email && (
                                        <p className="text-red-500 text-xs mt-1">
                                            Email is invalid
                                        </p>
                                    )}
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-zinc-300">
                                    Password
                                </label>

                                <div className="relative">
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        autoComplete="current-password"
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck="false"
                                        required
                                        placeholder="Enter your password"
                                        className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 pr-12 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#84ff00]"
                                    />
                                </div>
                            </div>



                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full rounded-2xl bg-purple-400 px-6 py-4 text-sm font-bold text-white transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60"
                                style={{ backgroundColor: "#7F00FF" }}
                            >
                                {isSubmitting ? "Logging in..." : "Login"}
                            </button>

                            <p className="text-center text-xs text-zinc-500">
                                Remember to join our Discord!
                            </p>
                            <p className="mt-4 text-sm text-zinc-500 text-center">
                                Forgot password?{" "}
                                <Link
                                    to="/reset"
                                    className="font-semibold text-[#84ff00]"
                                >
                                    Reset it here
                                </Link>
                            </p>
                        </form>
                    </div>
                    
                    <Link to={withNextPath(signupPath, nextPath)}>
                    <p className="mt-8 text-center text-sm text-white">
                        Don't have an account? Join here
                    </p>
                    </Link>
                </div>
            </div>
        </>
    );
}
